// src/components/notifications/AppSnackbar.tsx
//
// Custom notistack content component for the Smart Catalog app.
// Replaces notistack's default flat-saturated filled MUI Alert with an
// enterprise-grade card design:
//   - Bounded width (max 440px)
//   - White background with severity-colored left accent rail
//   - Severity icon at the start, close (X) at the end
//   - Optional title (bold) above the message
//   - Soft elevation + rounded corners
//
// Pass extra structured fields via notistack's `enqueueSnackbar(..., { title, ...})`
// — see `notifyPermissionDenied` / `notifyError` for the typed call sites.

import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { SnackbarContent, CustomContentProps, useSnackbar } from 'notistack';
import {
  CheckCircleOutline as SuccessIcon,
  InfoOutlined as InfoIcon,
  WarningAmberOutlined as WarningIcon,
  ErrorOutline as ErrorIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// Extra props we accept on top of notistack's CustomContentProps.
// Set them via enqueueSnackbar(message, { variant, title, ... } as any) —
// notistack accepts arbitrary extra option keys and passes them to the content.
interface ExtraProps {
  /** Bold one-line headline shown above the message. */
  title?: string;
}

declare module 'notistack' {
  interface VariantOverrides {
    // Use our custom appearance for all four built-in variants. notistack
    // applies our SnackbarContent regardless when registered via Components.
    default: false;
    success: { title?: string };
    info: { title?: string };
    warning: { title?: string };
    error: { title?: string };
  }
}

type Severity = 'default' | 'success' | 'info' | 'warning' | 'error';

const SEVERITY_STYLES: Record<
  Severity,
  { accent: string; iconColor: string; bg: string; titleColor: string }
> = {
  default: {
    accent: '#64748B',
    iconColor: '#64748B',
    bg: '#FFFFFF',
    titleColor: '#0F172A',
  },
  success: {
    accent: '#16A34A',
    iconColor: '#16A34A',
    bg: '#FFFFFF',
    titleColor: '#14532D',
  },
  info: {
    accent: '#0EA5E9',
    iconColor: '#0EA5E9',
    bg: '#FFFFFF',
    titleColor: '#0C4A6E',
  },
  warning: {
    accent: '#D97706',
    iconColor: '#D97706',
    bg: '#FFFFFF',
    titleColor: '#7C2D12',
  },
  error: {
    accent: '#DC2626',
    iconColor: '#DC2626',
    bg: '#FFFFFF',
    titleColor: '#7F1D1D',
  },
};

const SEVERITY_ICONS: Record<Severity, React.ComponentType> = {
  default: InfoIcon as unknown as React.ComponentType,
  success: SuccessIcon as unknown as React.ComponentType,
  info: InfoIcon as unknown as React.ComponentType,
  warning: WarningIcon as unknown as React.ComponentType,
  error: ErrorIcon as unknown as React.ComponentType,
};

const DEFAULT_TITLES: Record<Severity, string | undefined> = {
  default: undefined,
  success: undefined,
  info: undefined,
  warning: undefined,
  error: undefined,
};

interface Props extends CustomContentProps, ExtraProps {}

export const AppSnackbar = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { id, message, variant, title } = props;
  const { closeSnackbar } = useSnackbar();
  const severity: Severity = (variant ?? 'default') as Severity;
  const palette = SEVERITY_STYLES[severity];
  const Icon = SEVERITY_ICONS[severity];
  const resolvedTitle = title ?? DEFAULT_TITLES[severity];

  return (
    <SnackbarContent ref={ref} role="alert" aria-live="polite">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          minWidth: 320,
          maxWidth: 440,
          width: '100%',
          padding: '12px 12px 12px 14px',
          backgroundColor: palette.bg,
          borderRadius: 2,
          borderLeft: `4px solid ${palette.accent}`,
          boxShadow:
            '0 1px 2px rgba(15, 23, 42, 0.06), 0 8px 24px rgba(15, 23, 42, 0.08)',
          color: '#1E293B',
        }}
      >
        <Box
          sx={{
            color: palette.iconColor,
            mt: 0.25,
            flexShrink: 0,
            display: 'inline-flex',
            '& > svg': { fontSize: 22 },
          }}
        >
          <Icon />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {resolvedTitle && (
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: palette.titleColor, lineHeight: 1.35, mb: 0.25 }}
            >
              {resolvedTitle}
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{
              color: '#334155',
              lineHeight: 1.45,
              wordBreak: 'break-word',
            }}
          >
            {message}
          </Typography>
        </Box>

        <IconButton
          size="small"
          aria-label="Dismiss notification"
          onClick={() => closeSnackbar(id)}
          sx={{
            color: '#94A3B8',
            mt: -0.5,
            mr: -0.5,
            '&:hover': { color: '#1E293B', bgcolor: 'transparent' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </SnackbarContent>
  );
});

AppSnackbar.displayName = 'AppSnackbar';
